import React, { useEffect, useContext, useState, useMemo } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useParams, useHistory } from "react-router-dom";
import { IParams } from "@models/IParams";
import { ContextEDH } from "@context/ContextEDH";
import * as mouseflow from "@utils/mouseflow";
import Modal from "@components/modal";
import { Context } from "../../context/GroupsManagerContext";
import Donut from "../Donut/Donut";
import { Types as EditionTypes } from "../../context/editions.reducer";
import { Types as NavigationTypes } from "../../context/navigation.reducer";
import {
  GroupsTableFiltersState,
  setTableFiltersOrderBy,
  useGroupsTableFilters,
} from "../../context/useGroupsTableFilters";
import CapacityInput from "../CapacityInput/CapacityInput";
import MultiCapacityInput from "../MultiCapacityInput/MultiCapacityInput";
import savingAllowed from "../../utils/savingAllowed";
import { GroupsManagerSearchOrderByFields, OrderByDirection, PageInfo } from "@models/ISchema";
import { AdaptedGroup, GroupEditionErrors } from "../../models";
import { useSubgroupsPackagesColumns } from "../../hooks/useSubgroupsPackagesColumns";
import messageByGroupEditionError from "../../utils/messageByGroupEditionError";
import {
  Table,
  Link,
  Text,
  Heading,
  Button,
  SkeletonBase,
  Tooltip,
  Pager,
  OrderDirection,
  Pill,
} from "@foris/avocado-suite";
import { Icon } from "@foris/avocado-icons";
import { getColumnCell } from "@common/utils/table.util";
import DimensionsSearchSkeleton from "@components/dimensions-search-skeleton";
import { useTranslation } from "react-i18next";
import GroupTableCourseColumn from "@modules/vacancies/Groups/group-table-course-column";
import styles from "./subgroupsTable.module.scss";

interface Props {
  isGroupPackageType?: boolean;
  subgroups: AdaptedGroup[];
  errorReason?: keyof GroupEditionErrors;
  hasHardError?: boolean;
  requestSubgroups: (params: {
    page: number;
    pageSize?: number;
    avoidEditionsCleaning: boolean;
    orderBy: GroupsTableFiltersState["orderBy"];
    searchBy: GroupsTableFiltersState["searchBy"];
  }) => void;
  capacitiesSumUp: number;
  pageInfo: PageInfo;
  isLoading?: boolean;
  isLoadingCampuses?: boolean;
  pageSize?: number;
  setDisplayCreation: (display: boolean) => void;
  lastUpdateDateTime?: string;
}

const SubgroupsTable: React.FC<Props> = ({
  isGroupPackageType = false,
  capacitiesSumUp,
  pageInfo,
  errorReason,
  hasHardError,
  isLoading = false,
  isLoadingCampuses = false,
  pageSize,
  requestSubgroups,
  setDisplayCreation,
  subgroups,
  lastUpdateDateTime,
}) => {
  const { t } = useTranslation();
  const { state: outerState } = useContext(ContextEDH);
  const { state, dispatch } = useContext(Context);
  const tableFilters = useGroupsTableFilters();
  const history = useHistory();
  const [displayModal, setDisplayModal] = useState(false);
  const [groupIdsToExludeFromCapacityEditions, setGroupIdsToExludeFromCapacityEditions] = useState(
    new Set<string>(),
  );
  const [onModalClick, setOnModalClick] = useState({
    primary: () => {
      /* pass */
    },
    secondary: () => {
      /* pass */
    },
  });

  const isDefaultBehavior = outerState?.base?.groupsManagerType === "SHARED";

  const [subgroupPackageColumns] = useSubgroupsPackagesColumns({
    abilities: outerState?.base?.base?.user?.abilities,
    capacitiesSumUp,
    isDefaultBehavior,
  });

  const { origin, scenario, workspace }: IParams = useParams();
  const contextUrl = `${workspace}/${scenario}/${origin}`;

  const canEditGroupsInGroupManager =
    outerState?.base?.base?.user?.abilities?.can_edit_groups_in_group_manager;
  const canCreateSubgroups =
    outerState?.base?.base?.user?.abilities?.can_create_sub_groups_in_group_manager;

  const editionAllowed = (group: AdaptedGroup) =>
    group?.isEditable?.allowed && canEditGroupsInGroupManager;
  const groupIsVisible = (group: AdaptedGroup): boolean => {
    const editedVisibility = state?.editions?.byGroupId?.[group?.id]?.visibleForEnrollment;

    if (editedVisibility !== undefined) {
      return editedVisibility;
    }

    return group?.visibleForEnrollment ?? false;
  };

  /**
   * Given a callback, display the Modal component if any edition has been
   * made and the editions *can be saved*, asking for confirmation.
   *
   * If the user confirms, the given callback it's
   * executed, if the user "cancels" the callback isn't executed and the modal
   * disappears.
   */
  const confirmable =
    (callback: (...args: any[]) => any) =>
    (...args: any[]): any => {
      if (
        Object.keys(state?.editions?.byGroupId ?? {}).length > 0 &&
        savingAllowed(state?.editions?.errorsByGroupId)
      ) {
        setDisplayModal(true);
        setOnModalClick({
          primary: () => {
            setDisplayModal(false);
          },
          secondary: () => {
            setDisplayModal(false);
            dispatch({ type: EditionTypes.Clean });
            callback(...args);
          },
        });
      } else {
        callback(...args);
      }
    };

  useEffect(() => {
    if (state?.navigation?.forceFetching) {
      dispatch({ type: NavigationTypes.PopFromLocationStack });
    }
  }, [state?.navigation?.forceFetching]);

  const columnsSuite: any[] = isGroupPackageType
    ? subgroupPackageColumns
    : [
        {
          key: GroupsManagerSearchOrderByFields.GroupCode,
          isSortable: true,
          header: t("groups-manager.groups-table.table.headers.group-code-and-client-code"),
          renderCell: (row: unknown) => {
            const group = row as AdaptedGroup;
            const pathname = `/editor/groups-manager/${contextUrl}/${group?.id}`;

            if ("isResume" in group) {
              return (
                <Text weight="medium">
                  {t("groups-manager.groups-table.table.footer.total-capacity")}
                </Text>
              );
            }

            const onClick = () => {
              dispatch({
                type: NavigationTypes.AddToLocationStack,
                payload: {
                  view: `/editor/groups-manager/subgroups/${contextUrl}/${state?.data?.group?.id}`,
                  page: state?.data?.groupsPageInfo?.page ?? 1,
                },
              });
              mouseflow.actionTag(
                "action_groups_manager_enrollments",
                outerState?.base?.isMouseflowEnabled,
              );
              history.push(pathname);
            };

            return (
              <div className={styles.groupCode}>
                <Link
                  colored
                  disabled={!group?.code}
                  onClick={() => (group?.code ? onClick() : history.push(pathname))}
                >
                  {!group?.clientCode && !group?.code
                    ? "-"
                    : `${group?.code || ""}${
                        group?.clientCode && group?.code ? " | " : ""
                      }${group?.clientCode || ""}`}
                </Link>
                {group?.isReferent && (
                  <Pill>{t("groups-manager.groups-table.table.headers.main-group")}</Pill>
                )}
              </div>
            );
          },
        },
        !isDefaultBehavior
          ? {
              key: GroupsManagerSearchOrderByFields.Campus,
              isSortable: true,
              header: t("groups-manager.groups-table.table.headers.campus"),
              renderCell: (row: unknown) => {
                const group = row as AdaptedGroup;

                if ("isResume" in group) {
                  return "";
                }

                return getColumnCell(
                  group?.campus?.code || group?.campus?.name
                    ? `${
                        group?.campus?.code
                          ? `${group?.campus?.code} ${group?.campus?.name ? "| " : ""}`
                          : ""
                      }${group?.campus?.name ?? "-"}`
                    : "-",
                );
              },
            }
          : {
              key: GroupsManagerSearchOrderByFields.CourseName,
              isSortable: false,
              header: t("groups-manager.groups-table.table.headers.course"),
              renderCell: (row: unknown) => {
                const group = row as AdaptedGroup;

                if ("isResume" in group) {
                  return "";
                }

                const props = {
                  groupLabel: `${group?.course?.code} - ${group?.course?.name}`,
                  termCode: group?.term?.code,
                  campusCode: group?.campus?.code,
                  departmentCode: group?.curriculum?.program?.department?.code,
                  modalityCode: group?.curriculum?.program?.modality?.code,
                  shiftCode: group?.curriculum?.program?.shift?.code,
                  programCode: group?.curriculum?.program?.code,
                  curriculumCode: group?.curriculum?.code,
                };

                return <GroupTableCourseColumn {...props} hasOnboarding={false} />;
              },
              desktopOptions: {
                headCellStyles: {
                  width: "380px",
                  minWidth: "100px",
                },
              },
            },
        {
          key: GroupsManagerSearchOrderByFields.Capacity,
          isSortable: true,
          header: t("groups-manager.groups-table.table.headers.capacity"),
          renderCell: (row: unknown) => {
            const group = row as AdaptedGroup;

            if ("isResume" in group) {
              return (
                <div className={styles.capacityInputCol}>
                  {getColumnCell(capacitiesSumUp)}

                  {errorReason && (
                    <Tooltip
                      className={styles.capacityInputCol_tooltip}
                      label={messageByGroupEditionError(errorReason).abbreviated}
                    >
                      <Icon
                        color={hasHardError ? "error-50" : "warning-50"}
                        name={hasHardError ? "circle-cross" : "alert-triangle"}
                      />
                    </Tooltip>
                  )}
                </div>
              );
            }

            return (
              <div className={styles.capacityInputCol}>
                <CapacityInput
                  group={group}
                  disabled={!editionAllowed(group)}
                  isSubgroup={true}
                  inputClassname={styles.capacityInput}
                />
              </div>
            );
          },
        },
        {
          key: "utilization",
          header: t("groups-manager.groups-table.table.headers.utilization"),
          renderCell: group =>
            "isResume" in (group as any) ? "" : <Donut group={group as AdaptedGroup} />,
        },
        // {
        //   key: "groupType",
        //   header: "Tipo de grupo",
        //   renderCell: group => getPillCell(group?.referentType ?? ""),
        // },
      ];

  /**
   * Clean context's groupsToEdit when the table is initialized
   */
  useEffect(() => {
    dispatch({ type: EditionTypes.SetGroupsToEdit, payload: {} });
  }, []);

  /**
   * Set the `groupIdsToExludeFromCapacityEditions` after groups are requested.
   * Groups are exluded if are not editable.
   */
  useEffect(() => {
    const groupIdsToExclude = (state?.data?.groups ?? []).reduce((acc, group) => {
      if (!group?.isEditable?.allowed) {
        acc?.add(group?.id);
      }
      return acc;
    }, new Set<AdaptedGroup["id"]>());
    setGroupIdsToExludeFromCapacityEditions(groupIdsToExclude);
  }, [state?.data?.groups]);

  const hasAllGroupsSelected = useMemo(() => {
    return (subgroups ?? []).every(group => {
      return group?.id in (state?.editions?.groupsToEditById ?? {});
    });
  }, [state?.editions?.groupsToEditById, subgroups]);

  const subgroupsWithCount = useMemo(() => {
    if (!subgroups?.length) {
      return [];
    }

    return [
      ...subgroups,
      {
        isResume: true,
      },
    ];
  }, [subgroups]);

  const selectedGroupsToEdit = useMemo(() => {
    return Object.values(state?.editions?.groupsToEditById ?? {});
  }, [state?.editions?.groupsToEditById]);

  return (
    <section className={styles.tableContent}>
      <Modal
        typeState="confirm"
        title={t("groups-manager.not-saved-changes-modal.title")}
        show={displayModal}
        textButtonPrincipal={t("groups-manager.not-saved-changes-modal.principal-button")}
        textButtonSecondary={t("groups-manager.not-saved-changes-modal.secondary-button")}
        onClickPrincipal={onModalClick?.primary}
        onClickSecondary={onModalClick?.secondary}
        onClose={onModalClick?.primary}
      >
        <Text>{t("groups-manager.not-saved-changes-modal.description")}</Text>
      </Modal>

      <div className={styles.tableContent_header}>
        {!pageInfo?.size ? (
          <SkeletonBase height="24px" width="90px" />
        ) : (
          <Heading type="h2">
            {state?.data?.groupsPageInfo?.total === 1
              ? t("groups-manager.groups-table.count-title-single")
              : t("groups-manager.groups-table.count-title", {
                  count: state?.data?.groupsPageInfo?.total ?? 0,
                })}
          </Heading>
        )}

        <div className={styles.headerActions}>
          {!!lastUpdateDateTime && (
            <Text className={styles.headerActions_lastUpdate}>
              <b>{t("groups-manager.groups-table.last-update")}:</b> {lastUpdateDateTime}
            </Text>
          )}

          <Button
            size="sm"
            variant="ghost"
            leftIcon="refresh"
            disabled={!pageInfo?.size || isLoading}
            onClick={() =>
              requestSubgroups({
                page: state?.data?.groupsPageInfo?.page,
                avoidEditionsCleaning: true,
                orderBy: tableFilters.orderBy,
                searchBy: tableFilters.searchBy,
              })
            }
          >
            {t("groups-manager.groups-table.update-button")}
          </Button>

          {canCreateSubgroups && (
            <Button
              size="sm"
              variant="ghost"
              leftIcon="edit"
              disabled={!pageInfo?.size || isLoading || isLoadingCampuses}
              onClick={() => setDisplayCreation(true)}
            >
              {t("groups-manager.groups-table.edit-button")}
            </Button>
          )}
        </div>
      </div>

      {!pageInfo?.size || isLoading ? (
        <DimensionsSearchSkeleton showHeader={false} rows={5} columns={columnsSuite?.length} />
      ) : (
        <>
          <AnimatePresence>
            {selectedGroupsToEdit?.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className={styles.tableMultipleEdit}
              >
                <Heading type="h4" className={styles.tableMultipleEdit_title}>
                  {t("groups-manager.groups-table.actions.selected-rows", {
                    count: selectedGroupsToEdit?.length,
                  })}
                </Heading>

                <div className={styles.tableMultipleEdit_actions}>
                  <Button
                    size="sm"
                    leftIcon="open-eye"
                    variant="ghost"
                    disabled={Object.keys(state?.editions?.groupsToEditById ?? {}).length === 0}
                    onClick={() => {
                      dispatch({
                        type: EditionTypes.EditGroupsToEditVisibilities,
                        payload: state?.data?.groupsById,
                      });
                    }}
                  >
                    {t("groups-manager.groups-table.actions.change-visibility")}
                  </Button>

                  <MultiCapacityInput
                    disabled={Object.keys(state?.editions?.groupsToEditById ?? {}).length === 0}
                    getGroupIdsToExclude={groupIdsToExludeFromCapacityEditions}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <Table
            className={styles.tableContent_table}
            columns={columnsSuite}
            data={subgroupsWithCount}
            rowOptions={{
              selectorType: "checkbox",
              isAllSelected: hasAllGroupsSelected,
              isSelectorDisabled: row => !editionAllowed(row as AdaptedGroup),
              isSelected(row) {
                const group = row as AdaptedGroup;
                return group?.id in (state?.editions?.groupsToEditById ?? {});
              },
              onRowSelect: (selected, row) => {
                const group = row as AdaptedGroup;

                if (!selected) {
                  dispatch({ type: EditionTypes.RemoveGroupToEdit, payload: group });
                } else {
                  dispatch({ type: EditionTypes.AddGroupToEdit, payload: group });
                }
              },
              onSelectAll: selected => {
                if (!selected) {
                  dispatch({ type: EditionTypes.SetGroupsToEdit, payload: {} });
                  return;
                }

                const groupsToEditById = (state?.data?.groups ?? []).filter(editionAllowed).reduce(
                  (acc, group) => {
                    acc[group.id] = group;
                    return acc;
                  },
                  {} as Record<string, AdaptedGroup>,
                );

                dispatch({ type: EditionTypes.SetGroupsToEdit, payload: groupsToEditById });
              },
            }}
            rightActions={[
              {
                icon: row => (groupIsVisible(row as AdaptedGroup) ? "open-eye" : "closed-eye"),
                isDisabled: row => !editionAllowed(row as AdaptedGroup),
                onClick: row => {
                  const group = row as AdaptedGroup;

                  dispatch({
                    type: EditionTypes.UpdateGroupVisibility,
                    payload: {
                      groupId: group?.id,
                      value: !groupIsVisible(group),
                      originalValue: group?.visibleForEnrollment,
                    },
                  });
                },
              },
            ]}
            initialSort={{
              column: tableFilters.orderBy?.field ?? "",
              direction:
                (tableFilters.orderBy?.direction?.toLowerCase() as OrderDirection) ?? "desc",
            }}
            onSortChange={newSort => {
              const newOrderBy = {
                header: newSort?.column as any,
                field: newSort?.column as GroupsManagerSearchOrderByFields,
                direction: newSort?.direction?.toUpperCase() as OrderByDirection,
              };

              setTableFiltersOrderBy(newOrderBy);

              requestSubgroups({
                page: pageInfo?.page,
                avoidEditionsCleaning: false,
                orderBy: newOrderBy,
                searchBy: tableFilters.searchBy,
              });
            }}
          />
          {pageInfo && pageInfo?.size > 0 && (
            <Pager
              classname={{
                global: styles.tableContent_pager,
              }}
              size={pageSize ?? pageInfo?.size ?? 0}
              total={pageInfo?.total ?? 0}
              page={pageInfo?.page ?? 1}
              showRowsPerPage
              pageSizeOptions={[5, 10, 20]}
              onRowsPerPageChange={pageSize => {
                confirmable(requestSubgroups)({
                  page: 1,
                  pageSize: pageSize?.value,
                  avoidEditionsCleaning: false,
                  orderBy: tableFilters.orderBy,
                  searchBy: tableFilters.searchBy,
                });
              }}
              onChange={page =>
                confirmable(requestSubgroups)({
                  page,
                  avoidEditionsCleaning: false,
                  orderBy: tableFilters.orderBy,
                  searchBy: tableFilters.searchBy,
                })
              }
            />
          )}
        </>
      )}
    </section>
  );
};

export default SubgroupsTable;
